import { API_BASE_URL } from '@/config/env';
import type { DevRegAnswers } from '@/lib/devregStorage';
import { readDevRegAnswers } from '@/lib/devregStorage';
import type { NpdAwarenessAnswers } from '@/lib/npdAwarenessStorage';
import { readNpdAwarenessAnswers } from '@/lib/npdAwarenessStorage';
import type { SwitchAnswers } from '@/lib/switchStorage';
import { readSwitchAnswers } from '@/lib/switchStorage';
import type { SwitchReasonAnswers } from '@/lib/switchReasonStorage';
import { readSwitchReasonAnswers } from '@/lib/switchReasonStorage';
import { readTradeInAnswers } from '@/lib/tradeinStorage';
import type { TradeInAnswers } from '@/lib/tradeinStorage';
import { readSmokerProfileAnswers } from '@/lib/smokerProfileStorage';

function fetchFailureHint(): string {
  if (API_BASE_URL.startsWith('http')) {
    return ` Is the API running at ${API_BASE_URL}?`;
  }
  if (import.meta.env.DEV) {
    return ' Is the Vite dev server running (port 5174) with /api proxied to the backend on port 4000?';
  }
  return ' Check VITE_API_URL in .env.';
}

export type SurveySubmitBody = {
  race: string;
  state: string;
  brand: string;
  variant: string;
  devreg: DevRegAnswers;
  switchAnswer: SwitchAnswers;
  switchReason: SwitchReasonAnswers;
  npdAwareness: NpdAwarenessAnswers;
  tradein: TradeInAnswers;
  tradeInContact: {
    name: string;
    email: string;
    mobileLocalDigits: string;
  };
};

export type SurveySubmitTngResponse = {
  pin: string;
  expirydate: string | null;
  value: string | null;
  reused: boolean;
};

export function serializeSurveySubmitBody(body: SurveySubmitBody): SurveySubmitBody & {
  switch: SwitchAnswers;
} {
  const { switchAnswer, ...rest } = body;
  return { ...rest, switch: switchAnswer };
}

/**
 * POST /api/v1/customers/:customerUuid/survey-submit — persist full survey to Customer.
 * For `yes_help`, API also returns TNG fields for the voucher (same shape as survey-submit-tng).
 */
export async function submitCustomerSurvey(
  customerUuid: string,
  body: SurveySubmitBody
): Promise<SurveySubmitTngResponse | null> {
  const payload = serializeSurveySubmitBody(body);
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/v1/customers/${customerUuid}/survey-submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new Error(
      e instanceof Error && e.message === 'Failed to fetch'
        ? `Could not reach the server.${fetchFailureHint()}`
        : e instanceof Error
          ? e.message
          : 'Network error'
    );
  }
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    details?: unknown;
    pin?: string;
    expirydate?: string | null;
    value?: string | null;
    reused?: boolean;
  };
  if (!res.ok) {
    const msg =
      typeof data.error === 'string'
        ? data.error
        : `Save failed (${res.status}).`;
    const flat = data.details as { fieldErrors?: Record<string, unknown> } | undefined;
    const fe = flat?.fieldErrors;
    const hint =
      fe != null && Object.keys(fe).length > 0
        ? ` (${Object.entries(fe)
            .map(([k, v]) => `${k}: ${String(v)}`)
            .join('; ')})`
        : '';
    throw new Error(msg + hint);
  }
  if (data.ok !== true) {
    throw new Error('Unable to save survey');
  }
  if (typeof data.pin === 'string') {
    return {
      pin: data.pin,
      expirydate: data.expirydate ?? null,
      value: data.value ?? null,
      reused: data.reused === true,
    };
  }
  return null;
}

/** Builds the survey POST body from localStorage (same shape as trade-in contact submit). */
export function buildSurveySubmitBodyFromStorage(
  tradeInContact: SurveySubmitBody['tradeInContact']
): SurveySubmitBody | null {
  const smoker = readSmokerProfileAnswers();
  const devreg = readDevRegAnswers();
  const switchAnswer = readSwitchAnswers();
  const switchReason = readSwitchReasonAnswers();
  const npdAwareness = readNpdAwarenessAnswers();
  const tradein = readTradeInAnswers();

  if (
    !smoker?.race?.trim() ||
    !smoker?.state?.trim() ||
    !smoker?.brand?.trim() ||
    !smoker?.variant?.trim() ||
    !devreg ||
    !switchAnswer ||
    !switchReason ||
    !npdAwareness ||
    !tradein
  ) {
    return null;
  }

  return {
    race: smoker.race,
    state: smoker.state,
    brand: smoker.brand,
    variant: smoker.variant,
    devreg,
    switchAnswer,
    switchReason,
    npdAwareness,
    tradein,
    tradeInContact,
  };
}

/**
 * POST /api/v1/customers/:customerUuid/survey-submit-tng — full survey + assign TNG PIN.
 */
export async function submitCustomerSurveyTng(
  customerUuid: string,
  body: SurveySubmitBody
): Promise<SurveySubmitTngResponse> {
  const payload = serializeSurveySubmitBody(body);
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/v1/customers/${customerUuid}/survey-submit-tng`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    throw new Error(
      e instanceof Error && e.message === 'Failed to fetch'
        ? `Could not reach the server.${fetchFailureHint()}`
        : e instanceof Error
          ? e.message
          : 'Network error'
    );
  }
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    error?: string;
    details?: unknown;
    pin?: string;
    expirydate?: string | null;
    value?: string | null;
    reused?: boolean;
  };
  if (!res.ok) {
    const msg =
      typeof data.error === 'string'
        ? data.error
        : `Save failed (${res.status}).`;
    const flat = data.details as { fieldErrors?: Record<string, unknown> } | undefined;
    const fe = flat?.fieldErrors;
    const hint =
      fe != null && Object.keys(fe).length > 0
        ? ` (${Object.entries(fe)
            .map(([k, v]) => `${k}: ${String(v)}`)
            .join('; ')})`
        : '';
    throw new Error(msg + hint);
  }
  if (data.ok !== true || typeof data.pin !== 'string') {
    throw new Error('Unable to save survey or assign PIN');
  }
  return {
    pin: data.pin,
    expirydate: data.expirydate ?? null,
    value: data.value ?? null,
    reused: data.reused === true,
  };
}
